<template>
  <div class="similar-documents-list" data-testid="similar-documents-list">
    <div>
      <div v-if="getLoading">
        <i-spinner></i-spinner>
      </div>
      <!-- empty state -->
      <document-right-panel-empty-state
        v-else-if="listIsEmpty"
        data-testid="similar-docs-empty-state"
        symbol="medium-file"
        title="No similar documents available"
        sub-title="There are no similar documents in your workspace"
        class="empty-state"
      ></document-right-panel-empty-state>

      <div v-else class="documents-list d-flex flex-column">
        <!-- list -->
        <div
          v-for="similarDocument in getSimilarDocumentsList"
          :key="similarDocument.id"
          data-testid="document-item"
          class="document-item d-flex flex-row align-items-center"
        >
          <div
            class="thumbnail-container d-flex flex-row align-items-center i-pl-9 i-pr-9 i-pt-4 i-pb-4"
          >
            <image-with-loading
              :document="document"
              :loading="false"
              :img-src="similarDocument.thumbnail_url"
            ></image-with-loading>
          </div>
          <div class="names d-flex flex-column i-p-9">
            <div class="document-name">
              <router-link
                class="link i-text-subheading-3 i-text-neutral-120"
                data-testid="document-title"
                :to="`/customer/${similarDocument.customer.id}/document/${similarDocument.id}/signals`"
                @click="openDocumentRoute(similarDocument)"
              >
                {{ similarDocument.name }}
              </router-link>
            </div>
            <div class="document-title">
              <router-link
                class="link i-text-body-3 i-text-neutral-110"
                data-testid="document-title"
                :to="`/customers/${similarDocument.customer.id}`"
                @click="openCustomerRoute(similarDocument)"
              >
                {{ similarDocument.customer.name }}
              </router-link>
            </div>
          </div>
          <trust-score-display
            class="trust-score i-mr-9"
            :score="similarDocument.trust_score"
            :size="TrustScoreDisplaySizes.MEDIUM"
          />
          <i-button
            class="compare-button i-mr-6"
            text="Compare"
            type="neutral"
            subtype="outline"
            size="xs"
            @button-click="openCompareRoute(similarDocument)"
          ></i-button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { iButton, iSpinner } from '@inscribe/signal';
import { mapGetters } from 'vuex';
import TrustScoreDisplay, {
  TrustScoreDisplaySizes,
} from '@/V2/documents/TrustScoreDisplay';
import ImageWithLoading from '@/shared/components/ImageWithLoading.vue';
import DocumentRightPanelEmptyState from '@/V2/documents/document-view/DocumentRightPanelEmptyState';

export default {
  components: {
    DocumentRightPanelEmptyState,
    iButton,
    iSpinner,
    ImageWithLoading,
    TrustScoreDisplay,
  },
  props: {
    customerId: {
      type: String,
      required: true,
    },
    document: {
      type: Object,
      required: true,
    },
  },
  data: () => ({
    TrustScoreDisplaySizes,
  }),
  computed: {
    ...mapGetters({
      getSimilarDocumentsList: 'getSimilarDocumentsList',
      getLoading: 'getLoading',
    }),
    listIsEmpty() {
      return this.getSimilarDocumentsList.length === 0;
    },
  },
  methods: {
    openCompareRoute(similarDocument) {
      this.$router.push({
        name: 'document-compare',
        params: {
          customer_id: this.customerId,
          document_id: this.document.id,
        },
        query: {
          customerId: similarDocument.customer.id,
          documentId: similarDocument.id,
        },
      });
    },
    openDocumentRoute(similarDocument) {
      this.$router.push({
        name: 'document-signals',
        params: {
          customer_id: similarDocument.customer.id,
          document_id: similarDocument.id,
        },
      });
    },
    openCustomerRoute(similarDocument) {
      this.$router.push({
        name: 'customer-overview',
        params: {
          customer_id: similarDocument.customer.id,
        },
      });
    },
  },
};
</script>

<style lang="scss">
.documents-list {
  $borderRadius: map.get($radii, '2xl');

  border: 1px solid $light-neutral-60;
  border-radius: $borderRadius;

  .document-item {
    width: 100%;
    border-bottom: 1px solid $light-neutral-60;
    height: 76px;

    &:hover {
      background: $light-neutral-20;

      .compare-button {
        display: flex;
      }

      .trust-score {
        display: none;
      }
    }

    &:first-of-type {
      border-top-left-radius: $borderRadius;
      border-top-right-radius: $borderRadius;
      .thumbnail-container {
        border-top-left-radius: $borderRadius;
      }
    }

    &:last-of-type {
      border-bottom-left-radius: $borderRadius;
      border-bottom-right-radius: $borderRadius;
      border-bottom: 0;

      .thumbnail-container {
        border-bottom-left-radius: $borderRadius;
      }
    }

    .thumbnail-container {
      background: $light-neutral-30;
      width: 82px;
      height: 100%;

      img {
        max-width: 42px;
        max-height: 56px;
      }
    }

    .names {
      flex: 1;

      .document-name {
        height: 16px;
      }

      .link {
        display: flex;
        text-overflow: ellipsis;
        overflow: hidden;
        max-width: 180px;
        white-space: nowrap;

        cursor: pointer;
        &:hover {
          text-decoration: underline;
        }
      }
    }

    .trust-score {
      display: flex;
    }

    .compare-button {
      display: none;
      height: 24px;
    }
  }
}
</style>
